/**
 * Estado vivo do fluxo "gerar nova lista" (os projetos carregados do DinaBox,
 * antes de salvar). `ambientes`: [{ projectId, raw, items }].
 *
 * IMPORTANTE — persistencia em sessionStorage: o App.jsx envolve as rotas num
 * `<AnimatePresence><Routes key={location.pathname}>`, entao QUALQUER navegacao
 * (inclusive Projetos -> Editor) REMONTA a arvore de rotas e este provider.
 * Sem persistir, `ambientes` voltaria a [] no caminho para o Editor ("Nenhum
 * ambiente carregado"). Guardamos em sessionStorage (por aba, some ao fechar)
 * para sobreviver ao remount. Some ao recarregar a aba — comportamento igual
 * ao HTML original; uma vez salvo, vira historico persistido no backend.
 */
import React, { createContext, useCallback, useContext, useState } from "react";

const RevisorContext = createContext(null);

const STORAGE_KEY = "pharuerp:revisor:ambientes";

function lerInicial() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistir(valor) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(valor));
  } catch {
    /* quota excedida / storage indisponivel: mantem so em memoria */
  }
}

/**
 * Limpa o rascunho persistido SEM mexer no state React (evita re-render/flash
 * de empty-state). Chamar apos salvar a lista no backend: o rascunho ja virou
 * historico, entao reabrir /editor (back do navegador) nao deve reidratar uma
 * lista ja salva (evita salvar duplicado).
 */
export function descartarAmbientesPersistidos() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function RevisorProvider({ children }) {
  const [ambientes, setAmbientesState] = useState(lerInicial);

  // Persiste sincronamente ao atualizar (escreve antes do remount provocado
  // pela navegacao) para o Editor reidratar com os dados certos — inclusive as
  // EDICOES feitas no Editor (que escrevem de volta neste mesmo contexto).
  const setAmbientes = useCallback((next) => {
    setAmbientesState((prev) => {
      const valor = typeof next === "function" ? next(prev) : next;
      persistir(valor);
      return valor;
    });
  }, []);

  return (
    <RevisorContext.Provider value={{ ambientes, setAmbientes }}>
      {children}
    </RevisorContext.Provider>
  );
}

export function useRevisor() {
  const ctx = useContext(RevisorContext);
  if (!ctx) throw new Error("useRevisor fora do RevisorProvider");
  return ctx;
}
